import React, { useState, useEffect } from "react";
import { useNavigate } from "../i18n/router";
import { useAuth } from "../contexts/AuthContext";
import { Calendar, Clock, Heart, Loader2, Users, Sparkles } from "lucide-react";
import { EventMiniCard } from "../components/EventMiniCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  confirmed:  { label: "Подтверждено", color: "#065F46", bg: "#D1FAE5" },
  paid:       { label: "Оплачено",     color: "#1E3A8A", bg: "#DBEAFE" },
  requested:  { label: "Ожидает подтверждения", color: "#92400E", bg: "#FEF3C7" },
  interested: { label: "Интересно",    color: "#475569", bg: "#F1F5F9" },
  attended:   { label: "Посещено",     color: "#065F46", bg: "#D1FAE5" },
};

function AttendanceBadge({ status }) {
  const s = STATUS_STYLES[status];
  if (!s) return null;
  return (
    <span className="inline-block px-2.5 py-1 rounded-full text-[11px] font-medium mt-1"
      style={{ backgroundColor: s.bg, color: s.color }} data-testid={`my-status-${status}`}>
      {s.label}
    </span>
  );
}

function AttendingCard({ event }) {
  const navigate = useNavigate();
  return (
    <div
      className="eunoia-surface p-5 flex items-start gap-4 hover:-translate-y-0.5 transition-all cursor-pointer relative"
      onClick={() => navigate(`/events/${event.id}`)}
      data-testid={`attending-card-${event.id}`}
    >
      <div className="w-14 h-14 rounded-xl flex-shrink-0 flex flex-col items-center justify-center"
        style={{ backgroundColor: "var(--eunoia-accent-whisper)" }}>
        <span className="text-lg font-bold leading-none" style={{ color: "var(--eunoia-accent)" }}>
          {new Date(event.date).getDate()}
        </span>
        <span className="text-[10px] uppercase mt-0.5" style={{ color: "var(--eunoia-soft)" }}>
          {new Date(event.date).toLocaleDateString("ru-RU", { month: "short" })}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold truncate" style={{ color: "var(--eunoia-deep)" }}>{event.title}</h3>
        <p className="text-xs mt-0.5" style={{ color: "var(--eunoia-soft)" }}>
          {event.time}{event.city ? ` • ${event.city}` : ""}
        </p>
        <AttendanceBadge status={event.myStatus} />
      </div>
    </div>
  );
}

function OrganizingCard({ event }) {
  const navigate = useNavigate();
  return (
    <div
      className="eunoia-surface p-5 flex items-start gap-4 hover:-translate-y-0.5 transition-all cursor-pointer relative"
      data-testid={`organizing-card-${event.id}`}
    >
      <div className="w-14 h-14 rounded-xl flex-shrink-0 flex flex-col items-center justify-center"
        style={{ backgroundColor: "var(--eunoia-accent-whisper)" }}>
        <span className="text-lg font-bold leading-none" style={{ color: "var(--eunoia-accent)" }}>
          {new Date(event.date).getDate()}
        </span>
        <span className="text-[10px] uppercase mt-0.5" style={{ color: "var(--eunoia-soft)" }}>
          {new Date(event.date).toLocaleDateString("ru-RU", { month: "short" })}
        </span>
      </div>
      <div className="flex-1 min-w-0" onClick={() => navigate(`/events/${event.id}`)}>
        <h3 className="text-sm font-semibold truncate" style={{ color: "var(--eunoia-deep)" }}>{event.title}</h3>
        <p className="text-xs mt-0.5" style={{ color: "var(--eunoia-soft)" }}>
          {event.time}{event.city ? ` • ${event.city}` : ""}
        </p>
        <div className="flex gap-2 mt-1.5 flex-wrap">
          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium"
            style={{ backgroundColor: "#D1FAE5", color: "#065F46" }}>
            Подтверждено: {event.confirmedCount || 0}
          </span>
          {event.pendingCount > 0 && (
            <span className="px-2 py-0.5 rounded-full text-[11px] font-medium animate-pulse"
              style={{ backgroundColor: "#FEF3C7", color: "#92400E" }}>
              Заявок: {event.pendingCount}
            </span>
          )}
        </div>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); navigate(`/organizer/events/${event.id}/attendees`); }}
        className="flex-shrink-0 flex items-center gap-1 px-3 py-2 rounded-full text-xs font-medium text-white whitespace-nowrap"
        style={{ backgroundColor: "var(--eunoia-accent)" }}
        data-testid={`manage-attendees-link-${event.id}`}
      >
        <Users className="h-3 w-3" /> Участники
      </button>
    </div>
  );
}

const MyEventsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("upcoming");

  useEffect(() => {
    if (!authLoading && !user) navigate("/login?redirect=/my-events");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/my-events`, { credentials: "include" });
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [user]);

  if (authLoading || !user) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" style={{ color: "var(--eunoia-accent)" }} /></div>;
  }

  const isOrganizer = user.role === "organizer" || user.role === "admin";
  const organizingCount = data?.organizing?.length || 0;
  const pendingTotal = (data?.organizing || []).reduce((s, e) => s + (e.pendingCount || 0), 0);

  const tabs = [
    { key: "upcoming", label: "Предстоящие", icon: Calendar, count: data?.upcoming?.length || 0 },
    ...(isOrganizer ? [{ key: "organizing", label: "Я организую", icon: Sparkles, count: organizingCount, badge: pendingTotal }] : []),
    { key: "saved", label: "Сохраненные", icon: Heart, count: data?.saved?.length || 0 },
    { key: "past", label: "Прошедшие", icon: Clock, count: data?.past?.length || 0 },
  ];

  const renderList = () => {
    if (loading) {
      return <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin" style={{ color: "var(--eunoia-accent)" }} /></div>;
    }

    if (tab === "upcoming") {
      const list = data?.upcoming || [];
      if (list.length === 0) return emptyState("Нет предстоящих событий");
      return <div className="space-y-3">{list.map(ev => <AttendingCard key={ev.id} event={ev} />)}</div>;
    }

    if (tab === "past") {
      const list = data?.past || [];
      if (list.length === 0) return emptyState("Нет прошедших событий");
      return <div className="space-y-3">{list.map(ev => <AttendingCard key={ev.id} event={ev} />)}</div>;
    }

    if (tab === "saved") {
      const list = data?.saved || [];
      if (list.length === 0) return emptyState("Нет сохраненных событий");
      return <div className="space-y-3">{list.map(ev => <EventMiniCard key={ev.id} event={ev} saved={true} user={user} />)}</div>;
    }

    if (tab === "organizing") {
      const list = data?.organizing || [];
      if (list.length === 0) return emptyState("Вы пока не создали ни одного события", () => navigate("/add-event"), "Создать событие");
      return <div className="space-y-3">{list.map(ev => <OrganizingCard key={ev.id} event={ev} />)}</div>;
    }

    return null;
  };

  const emptyState = (msg, onClick, cta = "Найти события") => (
    <div className="eunoia-surface p-12 text-center">
      <Calendar className="h-12 w-12 mx-auto mb-4" style={{ color: "var(--eunoia-whisper)" }} />
      <p className="eunoia-body mb-4">{msg}</p>
      <button onClick={onClick || (() => navigate("/events"))} className="eunoia-btn-primary">{cta}</button>
    </div>
  );

  return (
    <div className="min-h-screen eunoia-space-lg px-4 pb-16" data-testid="my-events-page">
      <div className="max-w-3xl mx-auto">
        <h1 className="eunoia-title-large mb-8">Мои события</h1>

        <div className="flex gap-2 mb-6 flex-wrap">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all relative ${
                tab === t.key ? "text-white" : "bg-white/50 hover:bg-white/80"
              }`}
              style={{ backgroundColor: tab === t.key ? "var(--eunoia-accent)" : undefined }}
            >
              <t.icon className="h-4 w-4" /> {t.label}
              {t.count > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${tab === t.key ? 'bg-white/30' : ''}`}
                  style={{ backgroundColor: tab !== t.key ? 'var(--eunoia-accent-whisper)' : undefined, color: tab !== t.key ? 'var(--eunoia-accent)' : undefined }}>
                  {t.count}
                </span>
              )}
              {t.badge > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full text-[10px] font-bold flex items-center justify-center text-white"
                  style={{ backgroundColor: "#DC2626" }}>
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {renderList()}
      </div>
    </div>
  );
};

export default MyEventsPage;
